import type { OrganizationId } from '../containers/App/types';
import request, { apiURL, requestToDownload, post, get } from '../utils/api';
import { getCookie } from '../utils/cookie';

export default class UnitService {
  getAllByProperty(
    propertyId: string,
    organizationId: OrganizationId,
    fromUnit?: string,
  ) {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    const query = fromUnit ? `?fromUnit=${fromUnit}` : '';
    return request(
      `${apiURL}/${organizationId}/${propertyId}/unitAvailability${query}`,
      requestOptions,
    );
  }
  getAll(
    propertyId: string,
    organizationId: OrganizationId,
    onlyUnitData?: boolean = false,
  ) {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    const query = onlyUnitData === true ? `?onlyUnitData=${onlyUnitData}` : '';

    return request(
      `${apiURL}/${organizationId}/${propertyId}/units${query}`,
      requestOptions,
    );
  }
  getRentRoll(
    propertyId: string,
    organizationId: OrganizationId,
    options: Object,
  ) {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      ...options,
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/manageUnits/rentRoll`,
      requestOptions,
    );
  }
  getRentRollPaymentStatuses(
    propertyId: string,
    organizationId: OrganizationId,
    options: Object = {},
  ) {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      ...options,
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/manageUnits/rentRoll/paymentStatuses`,
      requestOptions,
    );
  }
  getCommercialRentRoll(propertyId: string, organizationId: OrganizationId) {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/commercialRentRoll`,
      requestOptions,
    );
  }
  getOccupiedUnitsForProperty(
    propertyId: string,
    organizationId: string,
    options: Object = {},
  ) {
    const requestOptions = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      ...options,
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/units/occupied`,
      requestOptions,
    );
  }

  /**
   * Units with resident name for HUD repayment agreement Unit/Resident dropdown.
   * @param {string} organizationId
   * @param {string} propertyId
   * @param {string} householdStatus - 'CURRENT_RESIDENT' or 'PRIOR_RESIDENT'
   * @returns {Promise<Array<{ unitId, householdId, unitNumber, hohFirstName, hohLastName, label }>>}
   */
  getUnitsWithResidentName(
    organizationId: string,
    propertyId: string,
    householdStatus: string,
    options: Object = {},
  ) {
    const requestOptions = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      ...options,
    };
    const query = `?householdStatus=${encodeURIComponent(householdStatus)}`;
    return request(
      `${apiURL}/${organizationId}/${propertyId}/units/with-resident-name${query}`,
      requestOptions,
    );
  }
  getById(
    propertyId: string,
    organizationId: OrganizationId,
    id: string,
    includeSoftDeleted?: boolean = false,
  ) {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/units/${id}${
        includeSoftDeleted ? '?includeSoftDeleted=true' : ''
      }`,
      requestOptions,
    );
  }

  getAccessibilityTypes(
    propertyId: string,
    organizationId: OrganizationId,
    options: Object = {},
  ) {
    return get(`/${organizationId}/${propertyId}/accessibility-types`, options);
  }

  getHudContractsForUnitAffordableSetup(
    propertyId: string,
    organizationId: OrganizationId,
    unitId: string,
    floorPlanId?: string,
    options: Object = {},
  ) {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      ...options,
    };

    const base = `${apiURL}/${organizationId}/${propertyId}/units/${unitId}/hud-contract-options`;
    const url = floorPlanId
      ? `${base}?floorPlanId=${encodeURIComponent(floorPlanId)}`
      : base;

    return request(url, requestOptions);
  }

  getEffectiveHudContract(
    propertyId: string,
    organizationId: OrganizationId,
    unitId: string,
    effectiveDate?: string,
    options: Object = {},
  ) {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      ...options,
    };

    const base = `${apiURL}/${organizationId}/${propertyId}/units/${unitId}/hud-contract`;
    const url = effectiveDate
      ? `${base}?effectiveDate=${encodeURIComponent(effectiveDate)}`
      : base;

    return request(url, requestOptions);
  }

  getPhProjectForUnitAffordableSetup(
    propertyId: string,
    organizationId: OrganizationId,
    unitId: string,
    options: Object = {},
  ) {
    const requestOptions = {
      ...options,
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    };

    return request(
      `${apiURL}/${organizationId}/${propertyId}/units/${unitId}/ph-project-assignment`,
      requestOptions,
    );
  }

  getFloorplanById(
    propertyId: string,
    organizationId: OrganizationId,
    id: string,
  ) {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/floorPlan/${id}`,
      requestOptions,
    );
  }
  getAllUnitStatuses() {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(`${apiURL}/unitStatuses`, requestOptions);
  }
  updateUnit(
    propertyId: string,
    organizationId: OrganizationId,
    unitId: string,
    payload: any = {},
    options?: Object,
  ) {
    const body = JSON.stringify(payload);
    return post(
      `/${organizationId}/${propertyId}/units/${unitId}`,
      body,
      options,
    );
  }
  getQuote(
    propertyId: string,
    organizationId: OrganizationId,
    unitId: string,
    userId: string,
    applicantId: ?string,
    prospectId: ?string,
  ) {
    // Include both applicantId and prospectId when present. The assigned-unit "Provide Quote"
    // path sends both; prospectId is the id the profile activity feed filters on, so dropping it
    // would leave the quote activity log unlinked to the household (and invisible on the profile).
    const search = new URLSearchParams({ userId });
    if (applicantId) search.set('applicantId', applicantId);
    if (prospectId) search.set('prospectId', prospectId);
    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/units/quote/${unitId}?${search.toString()}`,
    );
  }
  getCentralizedUnitAvailability(options: any) {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      ...options,
    };

    return request(`${apiURL}/allUnitAvailability`, requestOptions);
  }

  getUnitTypeOptions() {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };

    return request(`${apiURL}/unit-type-options`, requestOptions);
  }

  getHouseholdUnitHistory({
    organizationId,
    propertyId,
    unitId,
  }: {
    organizationId: string,
    propertyId: string,
    unit: string,
  }) {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/units/${unitId}/household-unit-history`,
      requestOptions,
    );
  }

  getUnitFloorPlanSchedule(
    organizationId: OrganizationId,
    propertyId: string,
    unitId: string,
  ) {
    const requestOptions = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };

    return request(
      `${apiURL}/${organizationId}/${propertyId}/units/${unitId}/floorplan-schedule`,
      requestOptions,
    );
  }

  getResidentHouseholdUnitFloorPlanAffordablePrograms(
    organizationId: string,
    propertyId: string,
    residentId: string,
  ) {
    return get(
      `/${organizationId}/${propertyId}/resident/${residentId}/get-household-unit-programs`,
    );
  }
}
